import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import type { Participant } from '../api/tenants';
import { getTenant } from '../api/tenants';
import * as proceduresApi from '../api/procedures';
import {
  getProcedureHiddenMetrics,
  getProcedureMetricReferences,
  type MetricReference,
} from '../api/sessionMetrics';
import * as cognitiveApi from '../api/cognitive';
import * as motionApi from '../api/motion';
import { SearchableSelect, sortOptions } from './SearchableSelect';

/**
 * Compare mode — head-to-head A-vs-B, procedure-scoped (Phases 2–3).
 *
 * A metric only compares meaningfully within one procedure, so this view
 * requires a procedure and two sides. Each side is a **participant**, an
 * **experience bucket**, or an **ad-hoc set** of participants. Group values
 * are the session-weighted mean of the members' per-participant means
 * (Σ mean·n / Σn) — which is the *exact* mean over the group's sessions, so
 * it's computed client-side from the existing procedure-scoped tenant
 * summaries; no dedicated endpoint needed.
 */

type Fmt = 'ratio' | 'duration_s' | 'percent' | 'angular_deg' | 'gyro_dps';
type Source = 'cog' | 'mot';
type Direction = 'higher' | 'lower' | 'none';

interface CmpMetric {
  key: string;
  label: string;
  source: Source;
  fmt: Fmt;
  direction: Direction;
}

// Drift-free + cognitive metrics only — linear path/speed are omitted (not
// trustworthy; see SENSOR-ONBOARD-FUSION-EVAL). Angular Path is the
// economy-of-motion metric.
const COMPARE_METRICS: CmpMetric[] = [
  { key: 'focus', label: 'Focus', source: 'cog', fmt: 'ratio', direction: 'higher' },
  { key: 'engagement', label: 'Emotional Engagement', source: 'cog', fmt: 'ratio', direction: 'higher' },
  { key: 'valence', label: 'Valence', source: 'cog', fmt: 'ratio', direction: 'higher' },
  { key: 'workload', label: 'Workload', source: 'cog', fmt: 'ratio', direction: 'lower' },
  { key: 'avg_duration_seconds', label: 'Avg Duration', source: 'cog', fmt: 'duration_s', direction: 'lower' },
  { key: 'total_angular_path_deg', label: 'Angular Path', source: 'mot', fmt: 'angular_deg', direction: 'none' },
  { key: 'active_duration_fraction', label: 'Active Duration', source: 'mot', fmt: 'percent', direction: 'higher' },
  { key: 'bidex_v2', label: 'Bimanual Coactivity (v2)', source: 'mot', fmt: 'ratio', direction: 'higher' },
  { key: 'idle_time_fraction', label: 'Idle Time', source: 'mot', fmt: 'percent', direction: 'lower' },
  { key: 'left_gyro_mean', label: 'Avg Gyro (left)', source: 'mot', fmt: 'gyro_dps', direction: 'none' },
  { key: 'right_gyro_mean', label: 'Avg Gyro (right)', source: 'mot', fmt: 'gyro_dps', direction: 'none' },
];

// Compare-metric key → the tenant `scorecard_hidden_metrics` key it maps to,
// for the few that differ from the raw summary field name (mirrors
// CognitivePerformanceGrid's mapping). Unlisted keys match 1:1; an unknown
// key just fails open (metric stays visible) rather than wrongly hiding it.
const COMPARE_KEY_TO_HIDDEN: Record<string, string> = {
  avg_duration_seconds: 'duration',
};

// Buckets by the participant's OWN self-reported years_of_experience — NOT
// the IRCAD faculty/mastery cohorts (Novice / Practice-ready / Expert), which
// are curated reference data in master_baselines. Deliberately labelled by
// year-range so the two can't be confused. Thresholds are arbitrary.
const EXPERIENCE_BUCKETS = [
  { key: 'exp_0_2', label: '≤ 2 yr experience', min: 0, max: 2 },
  { key: 'exp_3_7', label: '3–7 yr experience', min: 3, max: 7 },
  { key: 'exp_8_plus', label: '8+ yr experience', min: 8, max: Infinity },
] as const;

type SideKind = 'participant' | 'experience' | 'custom';
interface SideSpec {
  kind: SideKind;
  id: string | null; // participant
  bucket: string; // experience
  ids: string[]; // custom
}
const EMPTY_SIDE: SideSpec = { kind: 'participant', id: null, bucket: '', ids: [] };

function fmtValue(f: Fmt, v: number | null): string {
  if (v === null) return '—';
  switch (f) {
    case 'ratio':
      return v.toFixed(2);
    case 'duration_s':
      return `${Math.round(v)} sec`;
    case 'percent':
      return `${Math.round(v * 100)}%`;
    case 'angular_deg':
      return `${Math.round(v)}°`;
    case 'gyro_dps':
      return `${Math.round(v)} °/s`;
  }
}

function betterSide(
  direction: Direction,
  a: number | null,
  b: number | null,
): 'a' | 'b' | null {
  if (direction === 'none' || a === null || b === null || a === b) return null;
  return (direction === 'higher' ? a > b : a < b) ? 'a' : 'b';
}

type Breakdown = { participant_user_id: string; averages: Record<string, number | null> };

/** Session-weighted mean of a metric over the member ids — the exact mean
 *  over the group's sessions. Null when no member has data. */
export function aggregate(
  ids: string[],
  byParticipant: Breakdown[],
  key: string,
): number | null {
  let num = 0;
  let den = 0;
  for (const id of ids) {
    const row = byParticipant.find((p) => p.participant_user_id === id);
    if (!row) continue;
    const v = row.averages[key];
    const n = row.averages.session_count ?? 0;
    if (v === null || v === undefined || n <= 0) continue;
    num += v * n;
    den += n;
  }
  return den > 0 ? num / den : null;
}

/** Total distinct sessions a side covers (max of cog/mot count per member). */
function sideSessions(
  ids: string[],
  cog: Breakdown[],
  mot: Breakdown[],
): number {
  let total = 0;
  for (const id of ids) {
    const c = cog.find((p) => p.participant_user_id === id)?.averages.session_count ?? 0;
    const m = mot.find((p) => p.participant_user_id === id)?.averages.session_count ?? 0;
    total += Math.max(c ?? 0, m ?? 0);
  }
  return total;
}

// Side colours — distinct from the cohort ticks (novice purple / practice
// blue), so A/B never collide with a reference marker. A = teal, B = orange.
// The winning side's value is also bolded + green, so "who's better" reads
// without relying on colour alone.
const A_COLOR = '#00897b'; // teal
const B_COLOR = '#f57c00'; // orange

// Compare-metric key → the master_baselines metric key its references live
// under (the reference endpoint is keyed by the registry key). Unlisted keys
// match 1:1.
const COMPARE_KEY_TO_METRIC: Record<string, string> = {
  avg_duration_seconds: 'duration',
  total_angular_path_deg: 'total_angular_path',
};

// Cohort key → tick label + colour (mirrors the scorecard). Novice / practice
// -ready keep purple / blue.
const COHORT_META: Record<string, { label: string; color: string }> = {
  not_practice_ready: { label: 'Not practice-ready', color: '#ab47bc' },
  novice: { label: 'Not practice-ready', color: '#ab47bc' },
  beginner: { label: 'Not practice-ready', color: '#ab47bc' },
  practice_ready: { label: 'Practice-ready', color: '#42a5f5' },
  trainee: { label: 'Practice-ready', color: '#42a5f5' },
  competent: { label: 'Practice-ready', color: '#42a5f5' },
  proficient: { label: 'Practice-ready', color: '#42a5f5' },
  expert: { label: 'Expert', color: '#66bb6a' },
  attending: { label: 'Expert', color: '#66bb6a' },
  faculty: { label: 'Expert', color: '#66bb6a' },
  mastery: { label: 'Expert', color: '#66bb6a' },
};
function resolveCohort(c: string): { label: string; color: string } {
  return (
    COHORT_META[c] ?? {
      label: c.replace(/_/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase()),
      color: '#9e9e9e',
    }
  );
}

/** One metric = a full-width row (name · gauge · legend), stacked like the
 *  main-dashboard scorecard. The gauge carries the not-practice-ready /
 *  practice-ready reference ticks plus BOTH sides' markers on one worse→better
 *  axis, with the winning side highlighted. */
function CompareRow({
  m,
  av,
  bv,
  aLabel,
  bLabel,
  refs,
}: {
  m: CmpMetric;
  av: number | null;
  bv: number | null;
  aLabel: string;
  bLabel: string;
  refs: MetricReference[];
}) {
  const win = betterSide(m.direction, av, bv);
  const directional = m.direction !== 'none';
  const vals = [
    ...[av, bv].filter((v): v is number => v !== null),
    ...refs.map((r) => r.value),
  ];

  let track: React.ReactNode = null;
  if (vals.length > 0) {
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo || Math.abs(hi) || 1) * 0.12;
    const dmin = lo - pad;
    const dmax = hi + pad;
    const span = dmax - dmin || 1;
    const rawPct = (v: number) =>
      Math.max(0, Math.min(100, ((v - dmin) / span) * 100));
    // Every gauge reads worse (left) → better (right): flip lower-is-better.
    const pos = (v: number) =>
      m.direction === 'lower' ? 100 - rawPct(v) : rawPct(v);
    const sideMarker = (v: number | null, color: string, isWin: boolean) =>
      v === null ? null : (
        <Box
          key={color}
          sx={{
            position: 'absolute',
            top: -5,
            bottom: -5,
            left: `${pos(v)}%`,
            transform: 'translateX(-50%)',
            width: isWin ? 11 : 8,
            bgcolor: color,
            borderRadius: 1,
            zIndex: 2,
            boxShadow: (t) => `0 0 0 1.5px ${t.palette.background.paper}`,
          }}
        />
      );
    track = (
      <Box
        sx={{
          position: 'relative',
          height: 14,
          borderRadius: '7px',
          bgcolor: 'action.hover',
        }}
      >
        {/* Reference cohort ticks (context) — uniform solid. */}
        {refs.map((r) => {
          const { label, color } = resolveCohort(r.cohort);
          return (
            <Tooltip key={r.cohort} title={`${label}: ${fmtValue(m.fmt, r.value)}`}>
              <Box
                sx={{
                  position: 'absolute',
                  top: -3,
                  bottom: -3,
                  left: `${pos(r.value)}%`,
                  transform: 'translateX(-50%)',
                  width: 6,
                  borderRadius: 1,
                  bgcolor: color,
                  boxShadow: (t) => `0 0 0 1.5px ${t.palette.background.paper}`,
                }}
              />
            </Tooltip>
          );
        })}
        {sideMarker(av, A_COLOR, win === 'a')}
        {sideMarker(bv, B_COLOR, win === 'b')}
      </Box>
    );
  }

  const legendItem = (
    color: string,
    label: string,
    v: number | null,
    isWin: boolean,
  ) => (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
      />
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: isWin ? 800 : 600,
          color: isWin ? 'success.main' : 'text.primary',
        }}
      >
        {fmtValue(m.fmt, v)}
      </Typography>
    </Stack>
  );

  // De-dup the reference cohorts present, for the legend (one dot per label),
  // carrying the value so the number shows next to the marker.
  const refLegend = Array.from(
    new Map(
      refs.map((r) => {
        const { label, color } = resolveCohort(r.cohort);
        return [label, { color, value: r.value }] as const;
      }),
    ).entries(),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.75,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Box sx={{ width: { xs: 116, sm: 176 }, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
          {m.label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {track}
        {directional && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
              ‹ worse
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
              better ›
            </Typography>
          </Box>
        )}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}
        >
          {legendItem(A_COLOR, aLabel, av, win === 'a')}
          {legendItem(B_COLOR, bLabel, bv, win === 'b')}
          {refLegend.map(([label, { color, value }]) => (
            <Stack key={label} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {fmtValue(m.fmt, value)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

// Sections mirror the scorecard's Motion / Cognitive grouping.
const CMP_SECTIONS = [
  { key: 'mot', label: 'Motion (IMU)', match: (m: CmpMetric) => m.source === 'mot' },
  { key: 'cog', label: 'Cognitive (EEG)', match: (m: CmpMetric) => m.source === 'cog' },
];

// Side types read simplest → broadest (one person, a band, a hand-picked
// set) — a designed order, kept rather than alphabetized.
const SIDE_KIND_OPTIONS: { value: SideKind; label: string }[] = [
  { value: 'participant', label: 'Participant' },
  { value: 'experience', label: 'Experience level' },
  { value: 'custom', label: 'Custom group' },
];

// Experience bands run junior → senior; kept in that order.
const EXPERIENCE_OPTIONS = EXPERIENCE_BUCKETS.map((b) => ({
  value: b.key as string,
  label: b.label as string,
}));

const pName = (p: Participant) =>
  p.full_name ?? (p.has_email ? p.email : 'No name');

function SideControl({
  title,
  spec,
  onChange,
  roster,
}: {
  title: string;
  spec: SideSpec;
  onChange: (s: SideSpec) => void;
  roster: Participant[];
}) {
  const rosterOptions = useMemo(
    () => sortOptions(roster.map((p) => ({ value: p.id, label: pName(p) }))),
    [roster],
  );
  const customValue = useMemo(
    () => rosterOptions.filter((o) => spec.ids.includes(o.value)),
    [rosterOptions, spec.ids],
  );
  return (
    <Stack spacing={1} sx={{ minWidth: 220 }}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <SearchableSelect<SideKind>
        id={`${title}-kind`}
        label="Type"
        size="small"
        value={spec.kind}
        onChange={(kind) => onChange({ ...EMPTY_SIDE, kind })}
        options={SIDE_KIND_OPTIONS}
        keepOrder
      />

      {spec.kind === 'participant' && (
        <SearchableSelect
          id={`${title}-p`}
          label="Participant"
          size="small"
          value={spec.id ?? ''}
          onChange={(v) => onChange({ ...spec, id: v || null })}
          options={rosterOptions}
        />
      )}

      {spec.kind === 'experience' && (
        <>
          <SearchableSelect
            id={`${title}-e`}
            label="Experience"
            size="small"
            value={spec.bucket}
            onChange={(bucket) => onChange({ ...spec, bucket })}
            options={EXPERIENCE_OPTIONS}
            keepOrder
          />
          <Typography variant="caption" color="text.secondary">
            Groups your participants by self-reported years — not the faculty
            reference.
          </Typography>
        </>
      )}

      {spec.kind === 'custom' && (
        <Autocomplete
          id={`${title}-c`}
          multiple
          size="small"
          disableCloseOnSelect
          options={rosterOptions}
          value={customValue}
          onChange={(_e, opts) =>
            onChange({ ...spec, ids: opts.map((o) => o.value) })
          }
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderTags={(sel) => (
            <Typography variant="body2" component="span" sx={{ pl: 0.5 }}>
              {sel.length} selected
            </Typography>
          )}
          renderOption={(props, o) => {
            const { key, ...rest } = props as typeof props & { key: string };
            return (
              <li key={`${key}-${o.value}`} {...rest}>
                {o.label}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Participants" />
          )}
        />
      )}
    </Stack>
  );
}

export function CompareView({
  tenantId,
  participants,
}: {
  tenantId: string;
  participants: Participant[];
}) {
  const roster = useMemo(
    () => participants.filter((p) => p.is_active),
    [participants],
  );

  const [procedures, setProcedures] = useState<proceduresApi.Procedure[]>([]);
  const procedureOptions = useMemo(
    () => procedures.map((p) => ({ value: p.name, label: p.name })),
    [procedures],
  );
  const [procedure, setProcedure] = useState('');
  const [a, setA] = useState<SideSpec>(EMPTY_SIDE);
  const [b, setB] = useState<SideSpec>(EMPTY_SIDE);
  const [cog, setCog] = useState<cognitiveApi.CognitiveTenantSummary | null>(null);
  const [mot, setMot] = useState<motionApi.MotionTenantSummary | null>(null);
  const [loading, setLoading] = useState(false);
  // Per-metric reference cohorts for the selected procedure (keyed by master
  // metric key) — drives the not-practice-ready / practice-ready ticks.
  const [references, setReferences] = useState<
    Record<string, MetricReference[]>
  >({});

  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());
  // The tenant-wide default, kept so we can fall back when the selected
  // procedure has no override.
  const [tenantHidden, setTenantHidden] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    proceduresApi
      .listProcedures(tenantId)
      .then((ps) => !cancelled && setProcedures(ps))
      .catch(() => {});
    // The tenant's metric-visibility config gates which rows show.
    getTenant(tenantId)
      .then((t) => {
        if (cancelled) return;
        const def = new Set(t.scorecard_hidden_metrics);
        setTenantHidden(def);
        setHiddenMetrics(def);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Per-procedure override: this view is procedure-scoped, so the selected
  // procedure's own hidden set (if any) replaces the tenant default; else the
  // tenant default applies. `procedure` holds the procedure NAME.
  useEffect(() => {
    const proc = procedures.find((p) => p.name === procedure);
    if (!proc) {
      setHiddenMetrics(tenantHidden);
      return;
    }
    let cancelled = false;
    getProcedureHiddenMetrics(tenantId, proc.id)
      .then((o) => {
        if (cancelled) return;
        setHiddenMetrics(
          o.has_override ? new Set(o.hidden_metrics) : tenantHidden,
        );
      })
      .catch(() => {
        if (!cancelled) setHiddenMetrics(tenantHidden);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, procedure, procedures, tenantHidden]);

  const visibleMetrics = useMemo(
    () =>
      COMPARE_METRICS.filter(
        (m) => !hiddenMetrics.has(COMPARE_KEY_TO_HIDDEN[m.key] ?? m.key),
      ),
    [hiddenMetrics],
  );

  useEffect(() => {
    if (!procedure) {
      setCog(null);
      setMot(null);
      setReferences({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Reference cohorts for the selected procedure (best-effort — a procedure
    // with no baseline group just yields no ticks).
    const proc = procedures.find((p) => p.name === procedure);
    if (proc) {
      getProcedureMetricReferences(tenantId, proc.id)
        .then((r) => !cancelled && setReferences(r))
        .catch(() => !cancelled && setReferences({}));
    } else {
      setReferences({});
    }
    Promise.all([
      cognitiveApi.getTenantCognitiveSummary(tenantId, procedure),
      motionApi.getTenantMotionSummary(tenantId, procedure),
    ])
      .then(([c, m]) => {
        if (cancelled) return;
        setCog(c);
        setMot(m);
      })
      .catch(() => {
        if (!cancelled) {
          setCog(null);
          setMot(null);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId, procedure, procedures]);

  const resolve = (spec: SideSpec): { ids: string[]; label: string } => {
    if (spec.kind === 'participant') {
      const p = roster.find((x) => x.id === spec.id);
      return {
        ids: spec.id ? [spec.id] : [],
        label: p ? (p.full_name ?? (p.has_email ? p.email : p.id)) : '',
      };
    }
    if (spec.kind === 'experience') {
      const bucket = EXPERIENCE_BUCKETS.find((x) => x.key === spec.bucket);
      if (!bucket) return { ids: [], label: '' };
      const ids = roster
        .filter(
          (p) =>
            p.years_of_experience !== null &&
            p.years_of_experience >= bucket.min &&
            p.years_of_experience <= bucket.max,
        )
        .map((p) => p.id);
      return { ids, label: `${bucket.label} · ${ids.length}` };
    }
    return { ids: spec.ids, label: `Custom group · ${spec.ids.length}` };
  };

  const sideA = resolve(a);
  const sideB = resolve(b);
  const cogBy = (cog?.by_participant ?? []) as unknown as Breakdown[];
  const motBy = (mot?.by_participant ?? []) as unknown as Breakdown[];

  const ready = Boolean(procedure && sideA.ids.length && sideB.ids.length);

  const value = (m: CmpMetric, ids: string[]): number | null =>
    aggregate(ids, m.source === 'cog' ? cogBy : motBy, m.key);

  // Reference cohorts for a compare metric — map its key to the master metric
  // key the reference endpoint is keyed by.
  const refsFor = (m: CmpMetric): MetricReference[] =>
    references[COMPARE_KEY_TO_METRIC[m.key] ?? m.key] ?? [];

  return (
    <Stack spacing={2}>
      {/* Selection controls in their own card. */}
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Compare two sides on one procedure — each side can be a participant,
            an experience band (by years), or an ad-hoc group. Metrics only
            compare within a procedure, so pick one first.
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems="flex-start"
          >
            <SearchableSelect
              id="cmp-proc"
              label="Procedure"
              size="small"
              sx={{ minWidth: 200 }}
              value={procedure}
              onChange={setProcedure}
              options={procedureOptions}
            />
            <SideControl title="Side A" spec={a} onChange={setA} roster={roster} />
            <SideControl title="Side B" spec={b} onChange={setB} roster={roster} />
          </Stack>
        </CardContent>
      </Card>

      {!ready ? (
        <Alert severity="info" variant="outlined">
          Pick a procedure and two non-empty sides to compare.
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box>
          {/* Side labels + session counts, colour-coded to the gauge markers. */}
          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
          >
            <Chip
              size="small"
              sx={{ bgcolor: A_COLOR, color: '#fff', fontWeight: 700 }}
              label={`${sideA.label || 'A'} · ${sideSessions(sideA.ids, cogBy, motBy)} sess`}
            />
            <Chip
              size="small"
              sx={{ bgcolor: B_COLOR, color: '#fff', fontWeight: 700 }}
              label={`${sideB.label || 'B'} · ${sideSessions(sideB.ids, cogBy, motBy)} sess`}
            />
          </Stack>
          {/* Full-width rows, stacked and grouped by Motion / Cognitive —
              same shape as the main-dashboard scorecard. Each row's gauge
              carries the reference cohort ticks + both sides' markers. */}
          {CMP_SECTIONS.map((section) => {
            const secMetrics = visibleMetrics.filter(section.match);
            if (secMetrics.length === 0) return null;
            return (
              <Paper
                key={section.key}
                variant="outlined"
                sx={{
                  mt: 2,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 800, letterSpacing: '0.09em', color: 'primary.main' }}
                >
                  {section.label}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {secMetrics.map((m) => (
                    <CompareRow
                      key={m.key}
                      m={m}
                      av={value(m, sideA.ids)}
                      bv={value(m, sideB.ids)}
                      aLabel={sideA.label || 'A'}
                      bLabel={sideB.label || 'B'}
                      refs={refsFor(m)}
                    />
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Stack>
  );
}
